// Where Slowed runs. Pure: the background, the popup, the content script and
// the shortcuts all ask the same question of a URL.
import { isSafari } from "../platform";

export const YOUTUBE = "youtube";
export const YOUTUBE_MUSIC = "youtube-music";
export const SOUNDCLOUD = "soundcloud";
export const SPOTIFY = "spotify";

export const SOUNDCLOUD_URL = "https://soundcloud.com/";
export const SPOTIFY_URL = "https://open.spotify.com/";

// SoundCloud and Spotify are optional host permissions, asked for the first
// time Slowed is opened there. Listing them under host_permissions instead
// would show every existing install a new permission warning on update, and
// Chrome switches an extension off until that warning is accepted.
//
// Mobile Safari is sent to m.soundcloud.com. It is added on Safari only, for
// the same reason: on Chrome it would be one more warning on update. Keep it
// in step with safari/manifest.js.
export const MOBILE_SOUNDCLOUD_PAGES = ["*://m.soundcloud.com/*"];
export const SOUNDCLOUD_PAGES = [
  "*://soundcloud.com/*",
  ...(isSafari() ? MOBILE_SOUNDCLOUD_PAGES : []),
];
// w.soundcloud.com is SoundCloud's embedded player, which Slowed Studio
// (studio.slowed.app) plays pasted SoundCloud links in.
export const SOUNDCLOUD_EMBED_ORIGINS = ["*://w.soundcloud.com/*"];
export const SOUNDCLOUD_EMBEDS = ["*://w.soundcloud.com/player/*"];
// What is asked for: one prompt covers the site and the embedded player. Each
// half works on its own grant, so an install allowed on soundcloud.com before
// the player was added keeps working there.
export const SOUNDCLOUD_ORIGINS = [
  ...SOUNDCLOUD_PAGES,
  ...SOUNDCLOUD_EMBED_ORIGINS,
];

// Spotify's web player (open.spotify.com) and its embedded player, which
// Studio plays pasted Spotify links in, share one host: one grant covers
// both. Spotify plays on a phone in its app, not on the web, so there is no
// mobile host to add on Safari.
export const SPOTIFY_PAGES = ["*://open.spotify.com/*"];
export const SPOTIFY_EMBED_ORIGINS = SPOTIFY_PAGES;
export const SPOTIFY_EMBEDS = ["*://open.spotify.com/embed/*"];
export const SPOTIFY_ORIGINS = SPOTIFY_PAGES;

// Whether this build carries Spotify at all: every build does, as Spotify
// (Beta), off until the listener switches it on (./spotify-beta.js).
// webpack.common.js sets SLOWED_SPOTIFY per build (0 builds one without);
// a test run, with no setting, has it. Without it, open.spotify.com is a
// site Slowed does not know: nothing registered, asked for or offered.
export function isSpotifyEnabled() {
  const flag = process.env.SLOWED_SPOTIFY;
  return flag === undefined || flag === true || flag === "true" || flag === "1";
}

// The sites that run on a permission the listener grants (sites/access.js).
export const OPTIONAL_SITES = isSpotifyEnabled()
  ? [SOUNDCLOUD, SPOTIFY]
  : [SOUNDCLOUD];

const SOUNDCLOUD_HOSTS = new Set([
  "soundcloud.com",
  "www.soundcloud.com",
  ...(isSafari() ? ["m.soundcloud.com"] : []),
]);
const SOUNDCLOUD_EMBED_HOST = "w.soundcloud.com";
const SPOTIFY_HOST = "open.spotify.com";
const SPOTIFY_EMBED_PATH = "/embed/";

export function getSiteId(url) {
  let hostname;
  let pathname;
  try {
    ({ hostname, pathname } = new URL(url));
  } catch (error) {
    return null;
  }

  if (hostname === "music.youtube.com") return YOUTUBE_MUSIC;
  // Includes m.youtube.com, where Mobile Safari is sent (the Safari build's
  // manifest runs there; Chrome's does not, see safari/manifest.js).
  if (hostname === "youtube.com" || hostname.endsWith(".youtube.com")) {
    return YOUTUBE;
  }
  if (SOUNDCLOUD_HOSTS.has(hostname)) return SOUNDCLOUD;
  // The embedded player is not the site: a tab showing it has no panel.
  if (hostname === SPOTIFY_HOST && isSpotifyEnabled()) {
    return pathname.startsWith(SPOTIFY_EMBED_PATH) ? null : SPOTIFY;
  }
  return null;
}

export function isSoundCloudUrl(url) {
  return getSiteId(url) === SOUNDCLOUD;
}

export function isSpotifyUrl(url) {
  return getSiteId(url) === SPOTIFY;
}

// A site that runs on a permission the listener grants.
export function isOptionalSiteUrl(url) {
  return OPTIONAL_SITES.includes(getSiteId(url));
}

// SoundCloud's embedded player. Not a site of its own (a tab showing it has
// no panel): the content script treats it as SoundCloud inside Slowed Studio.
export function isSoundCloudEmbedUrl(url) {
  try {
    const { hostname, pathname } = new URL(url);
    return hostname === SOUNDCLOUD_EMBED_HOST && pathname.startsWith("/player");
  } catch (error) {
    return false;
  }
}

// Spotify's embedded player, the same way.
export function isSpotifyEmbedUrl(url) {
  if (!isSpotifyEnabled()) return false;
  try {
    const { hostname, pathname } = new URL(url);
    return hostname === SPOTIFY_HOST && pathname.startsWith(SPOTIFY_EMBED_PATH);
  } catch (error) {
    return false;
  }
}

// A site's embedded player: Slowed runs in one only when it sits directly in
// a Studio page (sites/studio.js).
export function isEmbedUrl(url) {
  return isSoundCloudEmbedUrl(url) || isSpotifyEmbedUrl(url);
}
